import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import ChartHeader from "./ChartHeader";

function ChartPartsChecked({ title, subtitle, data, orderNumber = "", dateRange = ""  }) {
    // Przykładowe dane - w prawdziwej aplikacji byłyby przekazane jako props
    const defaultData = [
        {
            "date": "n/a",
            "parts": [
                {
                    "partNumber": "n/a",
                    "totalChecked": 0
                }
            ]
        }
    ];

    const inputData = data || defaultData;

    // Znajdź wszystkie unikalne numery części
    const allPartNumbers = Array.from(new Set(
        inputData.flatMap(day => day.parts.map(part => part.partNumber))
    )).sort();

    // Przekształć dane do formatu wymaganego przez recharts
    const chartData = inputData.map(day => {
        // Usuń ostatnią część (rok) z daty
        const formattedDate = day.date.substring(0, day.date.lastIndexOf('/')); // "1/01/2026" -> "1/01"
        const dayData = { date: formattedDate };

        // Dla każdego numeru części, znajdź wartość lub ustaw 0
        allPartNumbers.forEach(partNumber => {
            const part = day.parts.find(p => p.partNumber === partNumber);
            dayData[partNumber] = part ? part.totalChecked : 0;
        });

        return dayData;
    });

    // Kolory dla różnych części (Material Design palette)
    const partColors = {
        0:   '#FBCEB1',
        1:   '#800080',
        2:   '#FFFF00',
        3:   '#808000',
        4:   '#DCF43C',
        5:   '#C0C0C0',
        6:   '#3FFFFF',
        7:   '#808080',
        8:   '#20B2AA',
        9:   '#C71585',
        10:  '#F5F5DC',
        11:  '#008B8B',
        12:  '#000F00',
        13:  '#87CEEB',
        14:  '#FA8072',
        15:  '#0F71F0',
        16:  '#4B0082',
        17:  '#000080',
        18:  '#118FF0',
        19:  '#E6E6FA',
        20:  '#00FFFF',
        21:  '#8B4513',
        22:  '#FF69B4',
        23:  '#00CED1',
        24:  '#FFD700'
    };

    // Oblicz statystyki
    const totalParts = chartData.reduce((sum, day) => {
        const dayTotal = allPartNumbers.reduce((daySum, partNumber) =>
            daySum + (day[partNumber] || 0), 0);
        return sum + dayTotal;
    }, 0);

    const activeDays = chartData.length;
    const activePartTypes = allPartNumbers.length;

    // Custom tooltip
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const activePayload = payload.filter(entry => entry.value > 0);
            const total = activePayload.reduce((sum, entry) => sum + entry.value, 0);

            return (
                <div style={{
                    backgroundColor: 'white',
                    padding: '12px',
                    border: '1px solid #ccc',
                    borderRadius: '6px',
                    fontSize: '13px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    minWidth: '180px'
                }}>
                    <p style={{ margin: '0 0 8px 0', fontWeight: '600', color: 'rgb(52, 71, 103)' }}>
                        {`Date: ${label}/2025`}
                    </p>
                    <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#666' }}>
                        {`Total Checked: ${total} parts`}
                    </p>
                    {activePayload.map((entry, index) => (
                        <div key={index} style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '4px'
                        }}>
                            <div style={{
                                width: '10px',
                                height: '10px',
                                backgroundColor: entry.color,
                                marginRight: '6px',
                                borderRadius: '2px'
                            }}></div>
                            <span style={{ color: '#333', fontSize: '12px' }}>
                                {entry.dataKey}: {entry.value}
                            </span>
                        </div>
                    ))}
                </div>
            );
        }
        return null;
    };

    // Custom legend
    const CustomLegend = ({ payload }) => {
        const activePayload = payload.filter(entry =>
            chartData.some(day => day[entry.value] > 0)
        );

        return (
            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: '16px',
                marginTop: '20px'
            }}>
                {activePayload.map((entry, index) => (
                    <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '13px',
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                    }}>
                        <div style={{
                            width: '12px',
                            height: '12px',
                            backgroundColor: entry.color,
                            marginRight: '6px',
                            borderRadius: '2px'
                        }}></div>
                        <span style={{ color: 'rgb(52, 71, 103)', fontWeight: '500' }}>
                            {entry.value}
                        </span>
                    </div>
                ))}
            </div>
        );
    };

    // Styles based on ContactContainer
    const styles = {
        container: {
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            padding: '24px',
            margin: '16px',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid rgba(0, 0, 0, 0.06)'
        },
        header: {
            marginBottom: '24px'
        },
        chartContainer: {
            backgroundColor: 'rgb(248, 249, 250)',
            borderRadius: '6px',
            padding: '24px',
            marginBottom: '28px',
            minHeight: '400px',
            display: 'flex',
            flexDirection: 'column'
        },
        statsContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
            padding: '0 20px'
        },
        statItem: {
            textAlign: 'center'
        },
        statNumber: {
            fontSize: '20px',
            fontWeight: '600',
            color: 'rgb(52, 71, 103)',
            margin: '0',
            fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
        },
        statLabel: {
            fontSize: '13px',
            color: 'rgb(123, 128, 154)',
            margin: '0',
            fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
        }
    };

    return (
        <div style={styles.container}>
            <ChartHeader
                title={title}
                subtitle={subtitle}
                orderName={orderNumber}
                dateRange={dateRange}
            />

            <div style={styles.chartContainer}>
                <div style={styles.statsContainer}>
                    <div style={styles.statItem}>
                        <p style={styles.statNumber}>{activeDays}</p>
                        <p style={styles.statLabel}>Analysis Days</p>
                    </div>
                    <div style={styles.statItem}>
                        <p style={styles.statNumber}>{totalParts}</p>
                        <p style={styles.statLabel}>Total Checked</p>
                    </div>
                </div>

                <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                        data={chartData}
                        margin={{
                            top: 20,
                            right: 30,
                            left: 20,
                            bottom: 5,
                        }}
                        maxBarSize={40}
                        barCategoryGap="20%"
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
                        <XAxis
                            dataKey="date"
                            tick={{ fontSize: 12, fill: 'rgb(123, 128, 154)' }}
                            axisLine={{ stroke: 'rgba(0,0,0,0.2)' }}
                        />
                        <YAxis
                            tick={{ fontSize: 12, fill: 'rgb(123, 128, 154)' }}
                            axisLine={{ stroke: 'rgba(0,0,0,0.2)' }}
                            label={{
                                value: 'Parts Checked',
                                angle: -90,
                                position: 'insideLeft',
                                style: { fontSize: '12px', fill: 'rgb(123, 128, 154)' }
                            }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend content={<CustomLegend />} />
                        {/*{allPartNumbers.map((partNumber, index) => {*/}
                        {/*    console.log("partNumber");*/}
                        {/*    console.log(partNumber);*/}
                        {/*})}*/}

                        {allPartNumbers.map((partNumber, index) => (
                            <Bar
                                key={partNumber}
                                dataKey={partNumber}
                                fill={partColors[index] || `hsl(${index * 137.5 % 360}, 70%, 50%)`}
                                radius={[2, 2, 0, 0]}
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

export default ChartPartsChecked;